import * as React from 'react';
import styled from 'styled-components';
import { FieldError } from 'react-hook-form';

import { LabelForm } from '../atoms';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (
    s.includes('issued') ||
    s.includes('approved') ||
    s.includes('completed') ||
    s.includes('pass') ||
    s.includes('paid')
  )
    return 'success';
  if (s.includes('progress') || s.includes('started') || s.includes('scheduled') || s.includes('inspection'))
    return 'info';
  if (s.includes('pending') || s.includes('waiting') || s.includes('sent') || s.includes('hold')) return 'warning';
  if (
    s.includes('denied') ||
    s.includes('rejected') ||
    s.includes('overdue') ||
    s.includes('emergency') ||
    s.includes('failed') ||
    s.includes('cancel')
  )
    return 'danger';
  return 'neutral';
};

interface Option {
  id: number | string;
  name: string;
}

interface Props {
  label: string;
  required?: boolean;
  options: Option[];
  value?: Option | string | null;
  onChange: (option: Option) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  error?: FieldError;
}

const StatusChipPicker = ({
  label,
  required,
  options,
  value,
  onChange,
  isLoading,
  isDisabled,
  error,
}: Props) => {
  const selectedId = value && typeof value === 'object' ? (value as Option).id : null;

  return (
    <Wrapper>
      <LabelForm htmlFor="activityStatus" isRequired={required}>
        {label}
      </LabelForm>
      <ChipRow data-disabled={isDisabled || undefined}>
        {isLoading && options.length === 0 && <Hint>Loading…</Hint>}
        {!isLoading && options.length === 0 && <Hint>No statuses available</Hint>}
        {options.map(opt => {
          const tone = toneByStatus(opt.name);
          const selected = selectedId === opt.id;
          return (
            <Chip
              key={opt.id}
              type="button"
              $tone={tone}
              $selected={selected}
              disabled={isDisabled}
              onClick={() => onChange(opt)}
              aria-pressed={selected}
            >
              <Dot $tone={tone} $selected={selected} />
              {opt.name}
            </Chip>
          );
        })}
      </ChipRow>
      {error && <ErrorText>{error.message}</ErrorText>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  label {
    margin-bottom: 8px;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  align-items: center;

  &[data-disabled] {
    opacity: 0.6;
  }
`;

const Hint = styled.span`
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const palette: Record<StatusTone, { fillBg: string; fillFg: string; outlineBorder: string; outlineFg: string; dot: string; hoverBg: string }> = {
  success: {
    fillBg: 'var(--success-600, #16a34a)',
    fillFg: 'var(--neutral-0)',
    outlineBorder: 'var(--success-200, #a7e0bd)',
    outlineFg: 'var(--success-700, #196a3b)',
    dot: 'var(--success-500, #22c55e)',
    hoverBg: 'var(--success-50, #e8f6ee)',
  },
  warning: {
    fillBg: 'var(--warning-500, #c08605)',
    fillFg: 'var(--neutral-0)',
    outlineBorder: 'var(--warning-200, #ffd980)',
    outlineFg: 'var(--warning-700, #8a5f05)',
    dot: 'var(--warning-500, #d99204)',
    hoverBg: 'var(--warning-50, #fff6e0)',
  },
  info: {
    fillBg: 'var(--primary-600)',
    fillFg: 'var(--neutral-0)',
    outlineBorder: 'var(--primary-200, #b6d8ec)',
    outlineFg: 'var(--primary-700, #0f5b86)',
    dot: 'var(--primary-500, #1e88c0)',
    hoverBg: 'var(--primary-50)',
  },
  danger: {
    fillBg: 'var(--danger-600, #b91c1c)',
    fillFg: 'var(--neutral-0)',
    outlineBorder: 'var(--danger-200, #f1b3b3)',
    outlineFg: 'var(--danger-700, #9a2523)',
    dot: 'var(--danger-500, #dc2626)',
    hoverBg: 'var(--danger-50, #fdecec)',
  },
  neutral: {
    fillBg: 'var(--neutral-700)',
    fillFg: 'var(--neutral-0)',
    outlineBorder: 'var(--border)',
    outlineFg: 'var(--neutral-700)',
    dot: 'var(--neutral-400)',
    hoverBg: 'var(--neutral-100)',
  },
};

const Chip = styled.button<{ $tone: StatusTone; $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;

  background: ${({ $tone, $selected }) => ($selected ? palette[$tone].fillBg : 'var(--neutral-0)')};
  color: ${({ $tone, $selected }) => ($selected ? palette[$tone].fillFg : palette[$tone].outlineFg)};
  border: 1px solid
    ${({ $tone, $selected }) => ($selected ? palette[$tone].fillBg : palette[$tone].outlineBorder)};
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none')};

  &:hover:not(:disabled) {
    background: ${({ $tone, $selected }) => ($selected ? palette[$tone].fillBg : palette[$tone].hoverBg)};
    border-color: ${({ $tone, $selected }) => ($selected ? palette[$tone].fillBg : palette[$tone].outlineBorder)};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Dot = styled.span<{ $tone: StatusTone; $selected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${({ $tone, $selected }) => ($selected ? 'rgba(255,255,255,0.95)' : palette[$tone].dot)};
  flex-shrink: 0;
`;

const ErrorText = styled.span`
  color: var(--danger-500, #f44336);
  font-size: 0.75rem;
  margin: 6px 2px 0;
`;

export default StatusChipPicker;
